import { Component } from '@theme/component';

/**
 * A tabbed panel group, following the same keyboard conventions as the theme's
 * layered slideshow tablist: roving tabindex, arrow keys to move, Home and End to
 * jump to the ends.
 *
 * Panels are plain elements toggled with the `hidden` attribute, so with the script
 * blocked every panel stays visible and the content is still fully readable.
 *
 * @typedef {Object} Refs
 * @property {HTMLButtonElement[]} [tabs]
 * @property {HTMLElement[]} [panels]
 *
 * @extends {Component<Refs>}
 */
class ProductTabs extends Component {
  #active = 0;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keydown', this.#handleKeydown);
    this.select(this.#initialIndex);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keydown', this.#handleKeydown);
  }

  get tabs() {
    return this.refs.tabs ?? [];
  }

  get panels() {
    return this.refs.panels ?? [];
  }

  get #initialIndex() {
    const marked = this.tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true');
    return marked === -1 ? 0 : marked;
  }

  /**
   * Shows one panel and hides the rest.
   *
   * @param {number|string} index - Zero-based index of the tab to activate.
   */
  select(index) {
    const next = Number.parseInt(String(index), 10);
    const { tabs, panels } = this;

    if (Number.isNaN(next) || next < 0 || next >= tabs.length) return;

    this.#active = next;

    tabs.forEach((tab, i) => {
      const selected = i === next;
      tab.setAttribute('aria-selected', String(selected));
      // Roving tabindex: only the active tab is in the tab order, so Tab moves past
      // the whole group rather than through every tab in it.
      tab.tabIndex = selected ? 0 : -1;
    });

    panels.forEach((panel, i) => {
      panel.toggleAttribute('hidden', i !== next);
    });
  }

  /** @param {KeyboardEvent} event */
  #handleKeydown = (event) => {
    if (!(event.target instanceof Element) || !event.target.closest('[role="tablist"]')) return;

    const count = this.tabs.length;
    if (!count) return;

    let next = null;

    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        next = (this.#active + 1) % count;
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        next = (this.#active - 1 + count) % count;
        break;
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = count - 1;
        break;
      default:
        return;
    }

    event.preventDefault();
    this.select(next);
    this.tabs[next]?.focus();
  };
}

if (!customElements.get('product-tabs-component')) {
  customElements.define('product-tabs-component', ProductTabs);
}
